'use client'

import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, type ReactNode } from 'react'

import { decodeFlagValues, defaultFlagValues, type FlagDefinitions, type FlagValues } from './definitions'

const FLAGS_QUERY_KEY = ['flags'] as const

/** Past this, the page renders on the defaults rather than stay blank. */
const FLAGS_TIMEOUT_MS = 3000

/** One retry absorbs a blip, so a tester does not get the defaults for a whole session. */
const FLAGS_RETRIES = 1

type FlagsProviderProps<Values> = { values: Values; children: ReactNode }

type FlagsLoaderProps = { fallback: ReactNode; children: ReactNode }

const FIRST_SERVER_ERROR_STATUS = 500

/** A 4xx from the route, such as a 401 for a visitor. Asking again gets the same answer. */
class FlagsRouteRefusal extends Error {}

const fetchServedValues = async (endpoint: string): Promise<unknown> => {
    const response = await fetch(endpoint, {
        credentials: 'same-origin',
        signal: AbortSignal.timeout(FLAGS_TIMEOUT_MS)
    })
    if (response.ok) return response.json()

    const message = `the flags route answered ${response.status}`
    throw response.status < FIRST_SERVER_ERROR_STATUS ? new FlagsRouteRefusal(message) : new Error(message)
}

const loadFlagValues = async <Definitions extends FlagDefinitions>(
    definitions: Definitions,
    endpoint: string
): Promise<FlagValues<Definitions>> => {
    let failure: unknown

    for (let attempt = 0; attempt <= FLAGS_RETRIES; attempt += 1) {
        try {
            return decodeFlagValues(definitions, await fetchServedValues(endpoint))
        } catch (error) {
            failure = error
            if (error instanceof FlagsRouteRefusal) break
        }
    }

    const reason = failure instanceof Error ? failure.message : 'unknown error'
    console.error(`flags: the flag values could not be loaded (${reason}), serving the defaults`)
    return defaultFlagValues(definitions)
}

/**
 * The browser side of an app's flags.
 *
 * `FlagsLoader` fetches the values the app's flags route decided for the
 * signed-in user, once per page load, and holds the tree until they land, so no
 * component renders on a guess. A flag changed in the dashboard applies on the
 * next load. When the route keeps failing, the tree renders on the defaults,
 * which never break a page: each default is either off or the production value.
 * `FlagsProvider` takes the values directly, which is what a test does.
 * `useFlags` reads them.
 */
export const createClientFlags = <Definitions extends FlagDefinitions>(definitions: Definitions, endpoint: string) => {
    const FlagsContext = createContext<FlagValues<Definitions> | null>(null)

    const FlagsProvider = ({ values, children }: FlagsProviderProps<FlagValues<Definitions>>) => (
        <FlagsContext.Provider value={values}>{children}</FlagsContext.Provider>
    )

    const FlagsLoader = ({ fallback, children }: FlagsLoaderProps) => {
        const { data } = useQuery({
            queryKey: FLAGS_QUERY_KEY,
            queryFn: () => loadFlagValues(definitions, endpoint),
            staleTime: Infinity,
            gcTime: Infinity,
            retry: false
        })

        if (!data) return fallback

        return <FlagsProvider values={data}>{children}</FlagsProvider>
    }

    const useFlags = (): FlagValues<Definitions> => {
        const values = useContext(FlagsContext)
        if (!values) throw new Error('useFlags needs a FlagsLoader or a FlagsProvider above it')

        return values
    }

    return { FlagsProvider, FlagsLoader, useFlags }
}
